// EditProjectFull.jsx
// Standalone page to edit a project (React Router v5 param :projectId)
// Fluent UI v8 + axios (plain JS)
//
// Editable fields:
// - project_number, title, client_id (client lookup), status, billable_amount,
//   summary_start_date, summary_end_date, currency, compass_pm_ref (person name),
//   canonical_description
//
// Manage links:
// - People linked to the project (default: project_staff_link) — adjust endpoints if yours differ
// - Proposals linked to the project (proposal_project_link)
// - Tags linked to the project (project_tags)
//
// Expected API (adjust as needed):
//   GET    /api/compass_projects/:projectId
//   PATCH  /api/compass_projects/:projectId           { ...fields }
//   GET    /api/clients?q=
//   GET    /api/people?q=
//   (PM ref) we just store the person name in compass_pm_ref; optionally add validation by id
//   People links:
//     GET    /api/compass_projects/:projectId/people
//     POST   /api/compass_projects/:projectId/people     { person_id, role? }
//     DELETE /api/compass_projects/:projectId/people/:personId
//   Proposal links:
//     GET    /api/compass_projects/:projectId/proposals
//     POST   /api/compass_projects/:projectId/proposals  { proposal_id }
//     DELETE /api/compass_projects/:projectId/proposals/:proposalId
//   Tags:
//     GET    /api/compass_projects/:projectId/tags
//     POST   /api/compass_projects/:projectId/tags       { tag_id }
//     DELETE /api/compass_projects/:projectId/tags/:tagId
//     GET    /api/tags?q=

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Separator,
  Label,
  Spinner,
  DatePicker,
  Dropdown,
  ComboBox,
  SearchBox,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IconButton,
} from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react';
import TagManager from '../components/TagManager';
import PeopleManager from '../components/PeopleManager';
import ProposalLinkManager from '../components/ProposalLinkManager';
import { styles } from './ProposalDashboard';

const api = axios.create({ baseURL: '/api' });

// const styles = mergeStyleSets({
//   page: { maxWidth: 1200, margin: '0 auto', padding: 16 },
//   card: { border: '1px solid var(--neutralLight,#e1e1e1)', borderRadius: 12, padding: 16 },
//   sectionTitle: { fontWeight: 600 },
// });

// ---------- Async Client ComboBox ----------
function AsyncClientCombo({ label = 'Client', value, onChange }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const r = await api.get('/clients', { params: q ? { q } : {} });
      const items = (Array.isArray(r.data.data) ? r.data.data : []).map(c => ({ key: c.id, text: c.name || c.client_name || c.title }));
      setOptions(items);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(''); }, [load]);

  return (
    <ComboBox
      label={label}
      selectedKey={value || null}
      options={options}
      allowFreeform={false}
      autoComplete="on"
      onChange={(_, option) => onChange(option ? option.key : '')}
      onPendingValueSet={(val) => { setText(val || ''); }}
      onInputValueChange={(_, val) => { setText(val || ''); }}
      // onMenuOpen={() => load(text)}
      // onResolveOptions={(opts) => { if (text && text.length >= 1) load(text); else load(''); return options; }}
      useComboBoxAsMenuWidth
      disabled={loading}
    />
  );
}

// ---------- Async PM (People) ComboBox (stores name in compass_pm_ref) ----------
function AsyncPMCombo({ label = 'PM (People)', value, onChange }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const r = await api.get('/people', { params: q ? { q } : {} });
      const items = (Array.isArray(r.data.data) ? r.data.data : []).map(p => ({ key: p.id, text: p.name || p.full_name || p.title }));
      setOptions(items);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(''); }, [load]);

  return (
    <ComboBox
      label={label}
      // Since DB keeps name, we try to match by text; otherwise keep current string
      text={value || ''}
      options={options}
      allowFreeform
      autoComplete="on"
      onChange={(_, option) => onChange(option ? option.text : value)}
      onInputValueChange={(_, val) => onChange(val || '')}
      // onMenuOpen={() => load(text)}
      onPendingValueSet={(val) => setText(val || '')}
      // onResolveOptions={(opts) => { if (text && text.length >= 1) load(text); else load(''); return options; }}
      useComboBoxAsMenuWidth
      disabled={loading}
      placeholder="Type to search people by name"
    />
  );
}

export default function EditProjectFull() {
  const { projectId } = useParams();
  const history = useHistory();

  const [form, setForm] = useState({
    project_number: '',
    title: '',
    client_id: '',
    status: '',
    billable_amount: '',
    currency: 'CAD',
    summary_start_date: null,
    summary_end_date: null,
    compass_pm_ref: '', // person name from people table
    canonical_description: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setError(''); setLoading(true);
    try {
      const r = await api.get(`/compass_projects/${projectId}`);
      const p = r.data || {};

      console.log({p});

      setForm({
        project_number: p.project_number || '',
        title: p.title || '',
        client_id: p.client_id || '',
        status: p.status || '',
        billable_amount: p.billable_amount ?? '',
        currency: p.currency || 'CAD',
        summary_start_date: p.summary_start_date ? new Date(p.summary_start_date) : null,
        summary_end_date: p.summary_end_date ? new Date(p.summary_end_date) : null,
        compass_pm_ref: p.compass_pm_ref || '',
        canonical_description: p.canonical_description || p.canonical_desciption || '',
      });
    } 
    catch (e) { 
      console.error(e); setError(e?.response?.data?.error || e.message || 'Failed to load'); 
    }
    finally { 
      setLoading(false); 
    }
  }, [projectId]);

  useEffect(() => { if (projectId) load(); }, [load, projectId]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload = {
        ...form,
        summary_start_date: form.summary_start_date ? form.summary_start_date.toISOString().slice(0,10) : null,
        summary_end_date: form.summary_end_date ? form.summary_end_date.toISOString().slice(0,10) : null,
      };
      await api.patch(`/compass_projects/${projectId}`, payload);
      setSuccess('Saved');
    } catch (e) { console.error(e); setError(e?.response?.data?.error || e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const statusOptions = ['Open', 'Closed'].map(s => ({ key: s, text: s }));

  return (
    <Stack className={styles.root} tokens={{childrenGap: 24}}>
      <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
        <Stack>
          <Text variant="xxLarge">Edit Project</Text>
          <Text variant="small" styles={{ root: { opacity: .7 } }}>Edit project details, people, proposals, and tags.</Text>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <PrimaryButton text={saving ? 'Saving…' : 'Save'} onClick={handleSave} disabled={saving} />
          <DefaultButton text="Back" onClick={() => history.goBack()} />
        </Stack>
      </Stack>

      {loading && <Spinner label="Loading project" />}
      {!loading && error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
      {!loading && success && <MessageBar messageBarType={MessageBarType.success}>{success}</MessageBar>}

      {!loading && !error && (
        <Stack tokens={{childrenGap: 16}}>
          {/* Core fields */}
          <Stack className={styles.sectionCard} tokens={{childrenGap: 16}}>
            <Text variant="xLarge">Project Details</Text>
            <Separator />
            <Stack tokens={{ childrenGap: 12 }}>
              <Stack horizontal tokens={{ childrenGap: 12 }}>
                <Stack.Item grow>
                  <TextField label="# Project" value={form.project_number} onChange={(_, v) => setField('project_number', v || '')} />
                </Stack.Item>
                <Stack.Item grow>
                  <TextField label="Title" value={form.title} onChange={(_, v) => setField('title', v || '')} />
                </Stack.Item>
              </Stack>

              <Stack horizontal tokens={{ childrenGap: 12 }}>
                <Stack.Item grow>
                  <AsyncClientCombo label="Client" value={form.client_id} onChange={(id) => setField('client_id', id)} />
                </Stack.Item>
                <Stack.Item grow>
                  <Dropdown label="Status" options={statusOptions} selectedKey={form.status} onChange={(_, o) => setField('status', o ? o.key : '')} />
                </Stack.Item>
              </Stack>

              <Stack horizontal tokens={{ childrenGap: 12 }}>
                <Stack.Item grow>
                  <TextField label="Billable Amount" value={String(form.billable_amount ?? '')} onChange={(_, v) => setField('billable_amount', v)} />
                </Stack.Item>
                <Stack.Item grow>
                  <Dropdown label="Currency" options={[{key:'CAD',text:'CAD'},{key:'USD',text:'USD'}]} selectedKey={form.currency} onChange={(_, o) => setField('currency', o ? o.key : 'CAD')} />
                </Stack.Item>
              </Stack>

              <Stack horizontal tokens={{ childrenGap: 12 }}>
                <Stack.Item grow>
                  <DatePicker label="Start Date" value={form.summary_start_date} onSelectDate={(d) => setField('summary_start_date', d)} placeholder="Select a date" />
                </Stack.Item>
                <Stack.Item grow>
                  <DatePicker label="End Date" value={form.summary_end_date} onSelectDate={(d) => setField('summary_end_date', d)} placeholder="Select a date" />
                </Stack.Item>
              </Stack>

              <Stack horizontal tokens={{ childrenGap: 12 }}>
                <Stack.Item grow>
                  <AsyncPMCombo label="Project Manager (People)" value={form.compass_pm_ref} onChange={(name) => setField('compass_pm_ref', name)} />
                </Stack.Item>
                <Stack.Item grow>
                  <TextField label="Canonical Description" multiline autoAdjustHeight value={form.canonical_description} onChange={(_, v) => setField('canonical_description', v || '')} />
                </Stack.Item>
              </Stack>
            </Stack>
          </Stack>

          <Stack className={styles.sectionCard}>
            <PeopleManager entityType="compass_projects" entityId={projectId} />
          </Stack>

          {/* Proposals Linker (proposal_project_link) */}
          <Stack className={styles.sectionCard}>
            <ProposalLinkManager entityType='compass_projects' entityId={projectId} />
          </Stack>

          {/* Tags */}
          <Stack className={styles.sectionCard}>
            <TagManager entityType="compass_projects" entityId={projectId} />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
